// src/views/kiddo-games/memory/Card.tsx
// Kopia src/components/Memory/Cards.tsx — karta tylko dla Memory Easy.
// Zamiast karty z napisem jest karta z dźwiękiem: po odwróceniu pokazuje głośnik, a słowo słychać.
import React, { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ReactCardFlip from 'react-card-flip';
import Tilt from 'react-parallax-tilt';
import classes from './Card.module.less';
import { ReactComponent as Leaf } from '../../../assets/icons/games/memory/leaf-outline.svg';
import { ReactComponent as Sound } from '../../../assets/icons/games/find/volume-high.svg';

type CardProps = {
  id: string;
  text?: string;
  image?: string;
  type?: 'sound' | 'image';
  isFinished?: boolean;
  isFlipped?: boolean;
  isDisabled?: boolean;
  handleClick?: (id: string) => void;
};

export const Card: FC<CardProps> = React.memo(({
  handleClick,
  text,
  image,
  type,
  id,
  isFlipped = false,
  isFinished = false,
  isDisabled = false,
}) => {
  const { t } = useTranslation();
  const onCardClick = useCallback(() => {
    if (!isDisabled && !isFinished && !isFlipped && handleClick) {
      handleClick(id);
    }
  }, [id, isDisabled, isFinished, isFlipped, handleClick]);

  return (
    <Tilt
      key={isFinished ? `finished-${id}` : `active-${id}`}
      tiltEnable={!isFinished && !isDisabled}
      tiltMaxAngleX={15}
      tiltMaxAngleY={15}
      scale={1.05}
    >
      <ReactCardFlip
        containerClassName={isFinished ? classes.cardFinished : ''}
        isFlipped={isFlipped || isFinished}
        flipDirection="vertical"
        flipSpeedFrontToBack={0.2}
        flipSpeedBackToFront={0.2}
      >
        <div
          className={`${classes.card} ${isDisabled ? classes.cardDisabled : ''}`}
          onClick={onCardClick}
          aria-hidden="true"
          id={id}
        >
          <Leaf className={classes.backLeaf} />
        </div>

        <div
          className={classes.cardTranslation}
          id={id}
        >
          {type === 'image' && image && (
            <img
              src={image}
              alt={text || t('games:labels.cardImage')}
              className={classes.cardImage}
            />
          )}
          {/* Głośnik — ta karta nie ma napisu, jej treścią jest wypowiedziane słowo */}
          {type === 'sound' && <Sound className={classes.soundIcon} />}
        </div>
      </ReactCardFlip>
    </Tilt>
  );
}, (prevProps, nextProps) => (prevProps.isFlipped === nextProps.isFlipped
    && prevProps.isFinished === nextProps.isFinished
    && prevProps.isDisabled === nextProps.isDisabled
    && prevProps.id === nextProps.id
));

Card.displayName = 'KiddoMemoryCard';

export default Card;
